import { actionHandlerWithFeatureFlag } from 'utils';
import { FEATURE_FLAGS } from 'utils/constants';
import DatadogHandler from 'utils/datadog';
import {
  msGetMarketingConsentDocumentUploadURL,
  msUpdateMarketingConsentDashboard,
} from 'services/RegistrationsService';
import {
  msCancelSchoolCentreDraft,
  msDeleteSchoolCentreDraft,
  msFetchCentreManagementConfig,
  msSubmitCentreDraft,
  msCreateOrUpdateSchoolCentreDraft,
  msCreateOrUpdateSchoolCentre,
} from 'services/CentreService';
import {
  FETCH_CENTRE_MANAGEMENT_CONFIG,
  CREATE_OR_UPDATE_CENTRE,
  CREATE_OR_UPDATE_CENTRE_DRAFT,
  DELETE_CENTRE_DRAFT,
  SUBMIT_CENTRE_DRAFT,
} from './actionTypes';
import {
  GET_CENTRE_MANAGEMENT_CONFIG,
  GET_MARKETING_CONSENT_UPLOAD_URL,
  UPDATE_MARKETING_CONSENT,
  CREATE_OR_UPDATE_SCHOOL_CENTRE_DRAFT,
  CREATE_OR_UPDATE_SCHOOL_CENTRE,
  DELETE_SCHOOL_CENTRE_DRAFT,
  CANCEL_SCHOOL_CENTRE_DRAFT,
  SUBMIT_SCHOOL_CENTRE_DRAFT,
} from '../models/centreManagementModel';
import { SkApolloRequest } from '../../utils/apolloUtils';

const fileName = 'src/redux/actions/CentreMgmtActions.js';

const dispatchFetchCentreManagementConfig = (
  dispatch,
  inProgress = true,
  data = [],
  error = null
) => {
  dispatch({
    type: FETCH_CENTRE_MANAGEMENT_CONFIG,
    key: 'centreManagementConfig',
    value: {
      inProgress,
      data,
      error,
    },
  });
};

const dispatchCreateOrUpdateCentre = (
  dispatch,
  inProgress = true,
  data = [],
  error = null
) => {
  dispatch({
    type: CREATE_OR_UPDATE_CENTRE,
    key: 'createOrUpdateCentre',
    value: {
      inProgress,
      data,
      error,
    },
  });
};

const dispatchCreateOrUpdateCentreDraft = (
  dispatch,
  inProgress = true,
  data = [],
  error = null
) => {
  dispatch({
    type: CREATE_OR_UPDATE_CENTRE_DRAFT,
    key: 'createOrUpdateCentreDraft',
    value: {
      inProgress,
      data,
      error,
    },
  });
};

const dispatchSubmitCentreDraft = (
  dispatch,
  inProgress = true,
  data = [],
  error = null
) => {
  dispatch({
    type: SUBMIT_CENTRE_DRAFT,
    key: 'submitCentreDraft',
    value: {
      inProgress,
      data,
      error,
    },
  });
};

const dispatchDeleteCentreDraft = (
  dispatch,
  inProgress = true,
  data = [],
  error = null
) => {
  dispatch({
    type: DELETE_CENTRE_DRAFT,
    key: 'deleteCentreDraft',
    value: {
      inProgress,
      data,
      error,
    },
  });
};

const dispatchCancelCentreDraft = (
  dispatch,
  inProgress = true,
  data = [],
  error = null
) => {
  dispatch({
    type: DELETE_CENTRE_DRAFT,
    key: 'cancelCentreDraft',
    value: {
      inProgress,
      data,
      error,
    },
  });
};

export const monoFetchCentreManagementConfig = (dispatch, { schoolID }) => {
  try {
    return SkApolloRequest({
      params: {
        query: GET_CENTRE_MANAGEMENT_CONFIG,
        variables: {
          fkSchool: schoolID,
        },
      },
      type: 'query',
    });
  } catch (err) {
    DatadogHandler.addError(err);
    DatadogHandler.sendLog(
      err,
      {
        functionName: 'monoFetchCentreManagementConfig',
        fileName,
      },
      'error'
    );
    dispatchFetchCentreManagementConfig(dispatch, false, null, {
      error: err.message,
    });
    return {
      success: false,
      error: [
        {
          message: err.message,
        },
      ],
    };
  }
};

export const fetchCentreManagementConfig = schoolID => async dispatch => {
  dispatchFetchCentreManagementConfig(dispatch);
  const result = await actionHandlerWithFeatureFlag(
    FEATURE_FLAGS.MS_PHASE_3_PORTAL_DISABLED,
    {
      dispatch,
      msHandler: msFetchCentreManagementConfig,
      monoHandler: monoFetchCentreManagementConfig,
      extraData: { schoolID },
    }
  );

  if (result.success) {
    dispatchFetchCentreManagementConfig(dispatch, false, result.data);
  } else {
    dispatchFetchCentreManagementConfig(dispatch, false, null, result.error);
  }
};

export const monoCreateOrUpdateSchoolCentre = async (dispatch, reqData) => {
  try {
    const {
      fkSchool,
      fkCentre,
      centreData,
      postcodes,
      levels,
      addressData,
      workplaces,
      centreAutoPromotionConfigs,
      centreLevelProgram,
      linkpointConfig,
    } = reqData;

    const result = await SkApolloRequest({
      params: {
        query: CREATE_OR_UPDATE_SCHOOL_CENTRE,
        variables: {
          schoolID: fkSchool,
          centreID: fkCentre,
          centreData,
          postcodes,
          levels,
          addressData,
          workplaces,
          centreAutoPromotionConfigs,
          centreLevelProgram,
          linkpointConfig,
        },
        type: 'mutation',
      },
    });
    return result;
  } catch (err) {
    DatadogHandler.addError(err);
    DatadogHandler.sendLog(
      err,
      {
        fileName,
        functionName: 'monoCreateOrUpdateSchoolCentre',
      },
      'error'
    );
    dispatchCreateOrUpdateCentre(dispatch, false, null, {
      error: err.message,
    });
    return { error: err.message };
  }
};

export const createOrUpdateSchoolCentre = reqData => async dispatch => {
  dispatchCreateOrUpdateCentre(dispatch);
  try {
    const result = await actionHandlerWithFeatureFlag(
      FEATURE_FLAGS.MS_PHASE_3_PORTAL_DISABLED,
      {
        dispatch,
        msHandler: msCreateOrUpdateSchoolCentre,
        monoHandler: monoCreateOrUpdateSchoolCentre,
        extraData: reqData,
      }
    );

    if (result.success) {
      dispatchCreateOrUpdateCentre(dispatch, false, result.data);
    } else {
      dispatchCreateOrUpdateCentre(dispatch, false, null, result.error);
    }
    return result;
  } catch (err) {
    dispatchCreateOrUpdateCentre(dispatch, false, null, {
      error: err.message,
    });
  }
};

export const monoGetMarketingConsentDocumentUploadURL = (_, reqData) => {
  try {
    return SkApolloRequest({
      params: {
        mutation: GET_MARKETING_CONSENT_UPLOAD_URL,
        variables: reqData,
      },
      type: 'mutation',
    });
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'monoGetMarketingConsentDocumentUploadURL',
        fileName,
      },
      'error'
    );
    return ex;
  }
};

export const getMarketingConsentDocumentUploadURL = reqData => async () => {
  const resp = await actionHandlerWithFeatureFlag(
    FEATURE_FLAGS.MS_PHASE_3_PORTAL_DISABLED,
    {
      msHandler: msGetMarketingConsentDocumentUploadURL,
      monoHandler: monoGetMarketingConsentDocumentUploadURL,
      extraData: reqData,
    }
  );
  return resp;
};

export const monoUpdateMarketingConsentDashboard = (_, reqData) => {
  try {
    return SkApolloRequest({
      params: {
        mutation: UPDATE_MARKETING_CONSENT,
        variables: reqData,
      },
      type: 'mutation',
    });
  } catch (ex) {
    DatadogHandler.addError(ex);
    DatadogHandler.sendLog(
      ex,
      {
        functionName: 'monoUpdateMarketingConsentDashboard',
        fileName,
      },
      'error'
    );
    return {
      error: [
        {
          message: ex.message,
        },
      ],
    };
  }
};

export const updateMarketingConsentDashboard = reqData => async () => {
  const resp = await actionHandlerWithFeatureFlag(
    FEATURE_FLAGS.MS_PHASE_3_PORTAL_DISABLED,
    {
      msHandler: msUpdateMarketingConsentDashboard,
      monoHandler: monoUpdateMarketingConsentDashboard,
      extraData: reqData,
    }
  );
  return resp;
};

export const monoCreateOrUpdateSchoolCentreDraft = async (
  dispatch,
  reqData
) => {
  try {
    const {
      fkSchool,
      fkCentre,
      centreData,
      postcodes,
      levels,
      addressData,
      workplaces,
      centreAutoPromotionConfigs,
      centreLevelProgram,
      linkpointConfig,
    } = reqData;

    const result = await SkApolloRequest({
      params: {
        query: CREATE_OR_UPDATE_SCHOOL_CENTRE_DRAFT,
        variables: {
          schoolID: fkSchool,
          centreID: fkCentre,
          centreData,
          postcodes,
          levels,
          addressData,
          workplaces,
          centreAutoPromotionConfigs,
          centreLevelProgram,
          linkpointConfig,
        },
        type: 'mutation',
      },
    });
    return result;
  } catch (err) {
    DatadogHandler.addError(err);
    DatadogHandler.sendLog(
      err,
      {
        fileName,
        functionName: 'monoCreateOrUpdateSchoolCentreDraft',
      },
      'error'
    );
    dispatchCreateOrUpdateCentreDraft(dispatch, false, null, {
      error: err.message,
    });
    return { error: err.message };
  }
};

export const createOrUpdateSchoolCentreDraft = reqData => async dispatch => {
  dispatchCreateOrUpdateCentreDraft(dispatch);
  try {
    const result = await actionHandlerWithFeatureFlag(
      FEATURE_FLAGS.MS_PHASE_3_PORTAL_DISABLED,
      {
        dispatch,
        msHandler: msCreateOrUpdateSchoolCentreDraft,
        monoHandler: monoCreateOrUpdateSchoolCentreDraft,
        extraData: reqData,
      }
    );

    if (result.success) {
      dispatchCreateOrUpdateCentreDraft(dispatch, false, result.data);
    } else {
      dispatchCreateOrUpdateCentreDraft(dispatch, false, null, result.error);
    }
    return result;
  } catch (err) {
    dispatchCreateOrUpdateCentreDraft(dispatch, false, null, {
      error: err.message,
    });
  }
};

export const monoDeleteSchoolCentreDraft = async (_, reqData) => {
  try {
    const { fkSchool, centreID } = reqData;
    return SkApolloRequest({
      params: {
        query: DELETE_SCHOOL_CENTRE_DRAFT,
        variables: {
          schoolID: fkSchool,
          centreID,
        },
        type: 'mutation',
      },
    });
  } catch (err) {
    DatadogHandler.addError(err);
    DatadogHandler.sendLog(
      err,
      {
        functionName: 'monoDeleteSchoolCentreDraft',
        fileName,
      },
      'error'
    );
    return {
      error: err.message,
    };
  }
};

export const deleteSchoolCentreDraft = reqData => async dispatch => {
  dispatchDeleteCentreDraft(dispatch);
  const result = await actionHandlerWithFeatureFlag(
    FEATURE_FLAGS.MS_PHASE_3_PORTAL_DISABLED,
    {
      msHandler: msDeleteSchoolCentreDraft,
      monoHandler: monoDeleteSchoolCentreDraft,
      dispatch,
      extraData: reqData,
    }
  );
  if (result.success) {
    dispatchCreateOrUpdateCentreDraft(dispatch, false, result.data);
  } else {
    dispatchCreateOrUpdateCentreDraft(dispatch, false, null, result.error);
  }
  return result;
};

export const monoCancelSchoolCentreDraft = async (_, reqData) => {
  try {
    const { fkSchool, centreID } = reqData;

    return SkApolloRequest({
      params: {
        query: CANCEL_SCHOOL_CENTRE_DRAFT,
        variables: {
          schoolID: fkSchool,
          centreID,
        },
        type: 'mutation',
      },
    });
  } catch (err) {
    DatadogHandler.addError(err);
    DatadogHandler.sendLog(
      err,
      {
        functionName: 'monoCancelSchoolCentreDraft',
        fileName,
      },
      'error'
    );
    return {
      error: err.message,
    };
  }
};

export const cancelSchoolCentreDraft = reqData => async dispatch => {
  dispatchCancelCentreDraft(dispatch);
  const result = await actionHandlerWithFeatureFlag(
    FEATURE_FLAGS.MS_PHASE_3_PORTAL_DISABLED,
    {
      msHandler: msCancelSchoolCentreDraft,
      monoHandler: monoCancelSchoolCentreDraft,
      dispatch,
      extraData: reqData,
    }
  );
  if (result.success) {
    dispatchCancelCentreDraft(dispatch, false, result.data);
  } else {
    dispatchCancelCentreDraft(dispatch, false, null, result.error);
  }
  return result;
};

export const monoSubmitCentreDraft = async (dispatch, reqData) => {
  try {
    const { fkSchool, centreID } = reqData;

    const result = await SkApolloRequest({
      params: {
        query: SUBMIT_SCHOOL_CENTRE_DRAFT,
        variables: {
          schoolID: fkSchool,
          centreID,
        },
        type: 'mutation',
      },
    });

    return result;
  } catch (err) {
    DatadogHandler.addError(err);
    DatadogHandler.sendLog(
      err,
      {
        fileName,
        functionName: 'monoSubmitCentreDraft',
      },
      'error'
    );
    throw err;
  }
};

export const submitSchoolCentreDraft = reqData => async dispatch => {
  dispatchSubmitCentreDraft(dispatch);
  try {
    const { fkSchool, centreID } = reqData;

    const result = await actionHandlerWithFeatureFlag(
      FEATURE_FLAGS.MS_PHASE_3_PORTAL_DISABLED,
      {
        dispatch,
        msHandler: msSubmitCentreDraft,
        monoHandler: monoSubmitCentreDraft,
        extraData: { fkSchool, centreID },
      }
    );

    if (result.success) {
      dispatchSubmitCentreDraft(dispatch, false, result.data);
    } else {
      dispatchSubmitCentreDraft(dispatch, false, null, result.error);
    }
    return result;
  } catch (err) {
    dispatchSubmitCentreDraft(dispatch, false, null, {
      error: err.message,
    });
  }
};

export default fetchCentreManagementConfig;
